import {
    AnyAction,
    CaseReducer,
    createAsyncThunk,
} from '@reduxjs/toolkit'
import {
    NinchatActionType,
    NinchatJoinChannelAction,
} from 'api/types/actions'
import {
    NinchatChannelJoinedEvent,
    NinchatEventType,
    NinchatRealmJoinedEvent,
} from 'api/types/events'
import ninchatClient, {
    NinchatActionParams,
    NinchatClientPromiseValue,
} from 'api/NinchatClient'
import {NinchatState} from 'api/types'
import {channelJoined} from 'api/event/channelJoined'
import {push} from 'navigation/actions'
import {realmJoined} from 'api/event/realmJoined'
import {startAppListening} from 'middlewares/listener'
import {updateRealm} from '../../state/ui'

const actionTypePrefix = 'NINCHAT/action'

type JoinChannelAsyncThunkConfig = {fulfilledMeta: {reducer: CaseReducer<NinchatState, AnyAction>}}

export const joinChannel = createAsyncThunk<NinchatClientPromiseValue, NinchatActionParams<NinchatJoinChannelAction>, JoinChannelAsyncThunkConfig>(
    `${actionTypePrefix}/${NinchatActionType.NinchatJoinChannelAction}`,
    async (arg, thunkAPI) => {
        const {...params} = arg

        params.action = NinchatActionType.NinchatJoinChannelAction

        const meta = {reducer: channelJoinedOrRealmJoinedReducer} // eslint-disable-line @typescript-eslint/no-use-before-define

        try {
            const events = await ninchatClient.send(params) as NinchatChannelJoinedEvent | (NinchatChannelJoinedEvent | NinchatRealmJoinedEvent)[]
            return thunkAPI.fulfillWithValue(events, meta)
        } catch (err) {
            return thunkAPI.rejectWithValue(err)
        }
    },
)

const channelJoinedOrRealmJoinedReducer: CaseReducer<NinchatState, AnyAction> = (state, action) => {
    const payloads = Array.isArray(action.payload) ? action.payload : [action.payload]

    for (const p of payloads) {
        const payload = typeof p.event === 'object' ? p : {event: p}

        const eventType = payload.event.event

        switch (eventType) {
            case NinchatEventType.NinchatChannelJoinedEvent:
                channelJoined.reducer(state, action)
                break
            case NinchatEventType.NinchatRealmJoinedEvent:
                realmJoined(state, action)
                break
            default:
        }
    }
}

const NAVIGATION_TIMEOUT_IN_MS = 5000

export const startJoinChannelListening = (isEmbedQueueChannel?: boolean) => {
    startAppListening({
        actionCreator: joinChannel.fulfilled,
        effect: async (action, listenerApi) => {
            listenerApi.cancelActiveListeners()

            const channel_id = Array.isArray(action.payload)
                ? (action.payload.find((p) => p.event === NinchatEventType.NinchatChannelJoinedEvent) as NinchatChannelJoinedEvent).channel_id // eslint-disable-line no-extra-parens
                : (action.payload as NinchatChannelJoinedEvent).channel_id // eslint-disable-line no-extra-parens

            if (!channel_id) {
                return
            }

            const isChannelInState = !!listenerApi.getState().ninchat.user_channels[channel_id]

            if (isChannelInState || await listenerApi.condition((_, currentState) => !!currentState.ninchat.user_channels[channel_id], NAVIGATION_TIMEOUT_IN_MS)) {
                if (!isEmbedQueueChannel) {
                    listenerApi.dispatch(push({
                        name: 'Channel',
                        params: {channel_id},
                    }))
                }

                const {realm_id} = listenerApi.getState().ninchat.user_channels[channel_id]

                if (realm_id && listenerApi.getState().ui.realm_id !== realm_id) {
                    listenerApi.dispatch(updateRealm(realm_id))
                }
            }
        },
    })
}
